import { useState, useMemo, useCallback, useEffect } from "react";
import { fetchVehicleList, saveVehiclePricing } from "../auth/service";

const fmt = (v) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.abs(v));

const makeKey = (model, variant, color) => `${model}::${variant}::${color}`;

function extractList(response) {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.data)) return response.data;
  if (response && response.data && Array.isArray(response.data.data))
    return response.data.data;
  return [];
}

const S = {
  root: {
    display: "grid",
    gridTemplateColumns: "42% 58%",
    gap: 12,
    padding: 12,
    minHeight: "100vh",
    background: "#f4f6f8",
    fontFamily: "'Inter', 'Segoe UI', sans-serif",
    fontSize: 13,
    color: "#1a2027",
    boxSizing: "border-box",
  },
  card: {
    background: "#fff",
    border: "1px solid #e0e3e8",
    borderRadius: 10,
    padding: 14,
    marginBottom: 10,
  },
  colHead: {
    fontSize: 13,
    fontWeight: 600,
    marginBottom: 12,
    paddingBottom: 8,
    borderBottom: "1px solid #eaecef",
    color: "#1a2027",
  },
  secLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#8c97a8",
    marginBottom: 10,
  },
  inlineField: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    padding: "6px 0",
    borderBottom: "1px solid #f0f2f5",
  },
  inlineLabel: { fontSize: 12, color: "#6b7a90", minWidth: 110, flexShrink: 0 },
  inlineInput: {
    flex: 1,
    padding: "4px 8px",
    fontSize: 13,
    border: "1px solid #e0e3e8",
    borderRadius: 6,
    background: "#f8f9fa",
    color: "#1a2027",
    outline: "none",
    boxSizing: "border-box",
  },
  mBtn: (active) => ({
    padding: "5px 14px",
    borderRadius: 6,
    border: active ? "1.5px solid #1976d2" : "1px solid #e0e3e8",
    background: active ? "#e3f0fc" : "#f4f6f8",
    color: active ? "#1565c0" : "#6b7a90",
    cursor: "pointer",
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    marginRight: 6,
    marginBottom: 6,
  }),
  colorBtn: (active, hex) => ({
    width: 28,
    height: 28,
    borderRadius: "50%",
    background: hex,
    border: active ? "3px solid #1565c0" : "2px solid #e0e3e8",
    cursor: "pointer",
    outline: active ? "2px solid #1565c0" : "none",
    outlineOffset: 2,
    flexShrink: 0,
  }),
  listItem: {
    display: "grid",
    gridTemplateColumns: "1fr 110px 32px",
    gap: 6,
    alignItems: "center",
    marginBottom: 6,
  },
  listInput: {
    padding: "5px 8px",
    fontSize: 12,
    border: "1px solid #e0e3e8",
    borderRadius: 6,
    background: "#f8f9fa",
    color: "#1a2027",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  delBtn: {
    border: "none",
    background: "none",
    color: "#c62828",
    cursor: "pointer",
    fontSize: 18,
    lineHeight: 1,
    borderRadius: 4,
    padding: "2px 4px",
  },
  addBtn: {
    border: "1px dashed #b0b8c4",
    background: "none",
    color: "#6b7a90",
    cursor: "pointer",
    fontSize: 12,
    padding: "5px 10px",
    borderRadius: 6,
    width: "100%",
    marginTop: 4,
  },
  specGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 6,
    marginBottom: 12,
  },
  specCell: {
    background: "#f4f6f8",
    borderRadius: 6,
    padding: "7px 9px",
    border: "1px solid #eaecef",
  },
  sLabel: {
    fontSize: 10,
    color: "#8c97a8",
    letterSpacing: 0.5,
    textTransform: "uppercase",
  },
  sVal: { fontSize: 13, fontWeight: 600, marginTop: 2 },
  priceLine: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "4px 0",
    borderBottom: "1px solid #eaecef",
  },
  netBox: {
    background: "#e3f0fc",
    border: "1px solid #90caf9",
    borderRadius: 8,
    padding: "12px 14px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 10,
  },
  saveBtn: {
    background: "#1976d2",
    color: "#fff",
    border: "none",
    borderRadius: 6,
    padding: "9px 18px",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    width: "100%",
    marginTop: 10,
  },
  warn: {
    background: "#fff8e1",
    color: "#b45309",
    fontSize: 12,
    padding: "6px 10px",
    borderRadius: 6,
    marginTop: 8,
    border: "1px solid #ffe082",
  },
  ok: {
    background: "#edf7ee",
    color: "#2e7d32",
    fontSize: 12,
    padding: "6px 10px",
    borderRadius: 6,
    marginTop: 8,
    border: "1px solid #a5d6a7",
  },
  loadingBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    height: 200,
    color: "#8c97a8",
    fontSize: 13,
    gridColumn: "1 / -1",
  },
  errorBox: {
    background: "#fff5f5",
    border: "1px solid #fecaca",
    borderRadius: 8,
    padding: 16,
    color: "#c62828",
    fontSize: 13,
    gridColumn: "1 / -1",
  },
};

function InlineField({ label, value, onChange, type = "text" }) {
  return (
    <div style={S.inlineField}>
      <span style={S.inlineLabel}>{label}</span>
      <input
        style={S.inlineInput}
        type={type}
        value={value}
        onChange={(e) =>
          onChange(type === "number" ? +e.target.value : e.target.value)
        }
        onFocus={(e) => (e.target.style.borderColor = "#1976d2")}
        onBlur={(e) => (e.target.style.borderColor = "#e0e3e8")}
      />
    </div>
  );
}

function ListEditor({ items, onChange, isDiscount }) {
  const update = (i, field, val) =>
    onChange(
      items.map((item, idx) =>
        idx === i ? { ...item, [field]: field === "value" ? +val : val } : item,
      ),
    );
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const add = () =>
    onChange([...items, { name: "", value: isDiscount ? -1000 : 1000 }]);

  return (
    <div>
      {items.map((item, i) => (
        <div key={i} style={S.listItem}>
          <input
            style={S.listInput}
            type="text"
            value={item.name}
            placeholder="Name"
            onChange={(e) => update(i, "name", e.target.value)}
          />
          <input
            style={{
              ...S.listInput,
              color: isDiscount ? "#2e7d32" : "#b45309",
              fontWeight: 600,
            }}
            type="number"
            value={item.value}
            onChange={(e) => update(i, "value", e.target.value)}
          />
          <button style={S.delBtn} onClick={() => remove(i)}>
            ×
          </button>
        </div>
      ))}
      <button style={S.addBtn} onClick={add}>
        + Add {isDiscount ? "discount" : "add-on"}
      </button>
    </div>
  );
}

function PriceLine({ label, value, bold, isDiscount }) {
  return (
    <div style={S.priceLine}>
      <span
        style={{
          fontSize: bold ? 14 : 13,
          color: bold ? "#1a2027" : "#6b7a90",
          fontWeight: bold ? 600 : 400,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: bold ? 14 : 13,
          fontWeight: 600,
          color: isDiscount ? "#2e7d32" : bold ? "#1a2027" : "#6b7a90",
        }}
      >
        {value < 0 ? `− ${fmt(value)}` : fmt(value)}
      </span>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export default function VehiclePricing() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [allDocs, setAllDocs] = useState({});
  const [selModel, setSelModel] = useState("");
  const [selVariant, setSelVariant] = useState("");
  const [selColor, setSelColor] = useState("");
  const [saved, setSaved] = useState(false);

  const fetchVehicles = async () => {
    return fetchVehicleList()
      .then((response) => {
        const list = extractList(response);
        if (!list.length) {
          setError(
            "No vehicles returned from the API. Check the endpoint or network tab.",
          );
          return;
        }

        setVehicles(list);

        const docs = {};
        list.forEach((v) => {
          const k = makeKey(v.model, v.variant, v.color);
          docs[k] = {
            ...v,
            addOnPrices: v.addOnPrices ? [...v.addOnPrices] : [],
            discountPrices: v.discountPrices ? [...v.discountPrices] : [],
          };
        });
        setAllDocs(docs);

        const uniqueModels = [...new Set(list.map((v) => v.model))];
        if (uniqueModels.length > 0) {
          const firstModel = uniqueModels[0];
          const variantsForModel = [
            ...new Set(
              list.filter((v) => v.model === firstModel).map((v) => v.variant),
            ),
          ]
            .sort()
            .reverse();
          const firstVariant = variantsForModel[0] || "";
          const colorsForVariant = list
            .filter((v) => v.model === firstModel && v.variant === firstVariant)
            .map((v) => v.color);
          setSelModel(firstModel);
          setSelVariant(firstVariant);
          setSelColor(colorsForVariant[0] || "");
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const models = useMemo(
    () => [...new Set(vehicles.map((v) => v.model))],
    [vehicles],
  );

  const variants = useMemo(
    () =>
      selModel
        ? [
            ...new Set(
              vehicles
                .filter((v) => v.model === selModel)
                .map((v) => v.variant),
            ),
          ]
            .sort()
            .reverse()
        : [],
    [vehicles, selModel],
  );

  const currentKey = makeKey(selModel, selVariant, selColor);
  const doc = allDocs[currentKey];

  const computed = useMemo(() => {
    if (!doc) return null;
    const addOnTotal = doc.addOnPrices.reduce((s, a) => s + (+a.value || 0), 0);
    const discTotal = doc.discountPrices.reduce(
      (s, d) => s + (+d.value || 0),
      0,
    );
    const totalPrice = (+doc.mrp || 0) + addOnTotal;
    const netPayable = totalPrice + discTotal;
    const warns = [];
    if (netPayable <= 0) warns.push("Net payable is zero or negative");
    if (discTotal < -totalPrice) warns.push("Discounts exceed total price");
    return { addOnTotal, discTotal, totalPrice, netPayable, warns };
  }, [doc]);

  const update = useCallback(
    (field, val) =>
      setAllDocs((prev) => ({
        ...prev,
        [currentKey]: { ...prev[currentKey], [field]: val },
      })),
    [currentKey],
  );

  const handleSelectModel = (model) => {
    setSelModel(model);
    const newVariants = [
      ...new Set(
        vehicles.filter((v) => v.model === model).map((v) => v.variant),
      ),
    ]
      .sort()
      .reverse();
    const fv = newVariants[0] || "";
    setSelVariant(fv);
    setSelColor(
      vehicles
        .filter((v) => v.model === model && v.variant === fv)
        .map((v) => v.color)[0] || "",
    );
  };

  const handleSelectVariant = (variant) => {
    setSelVariant(variant);
    setSelColor(
      vehicles
        .filter((v) => v.model === selModel && v.variant === variant)
        .map((v) => v.color)[0] || "",
    );
  };

  const handleSave = async () => {
    if (!doc || !computed) return;

    try {
      const payload = {
        model: selModel,
        variant: selVariant,
        mrp: doc.mrp,
        exShowroomPrice: computed.netPayable,
        addOnPrices: doc.addOnPrices,
        discountPrices: doc.discountPrices,
        showPrices: doc.showPrices || [],
        dealName: doc.dealName || "",
        totalDiscount: computed.discTotal,
        totalPrice: computed.totalPrice,
      };

      const result = await saveVehiclePricing(payload);

      if (result.error) {
        console.error("Save failed:", result.message);
        return;
      }

      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      fetchVehicles();
    } catch (err) {
      console.error("Save error:", err);
    }
  };
  if (loading)
    return (
      <div style={S.root}>
        <div style={S.loadingBox}>Loading vehicle data…</div>
      </div>
    );
  if (error)
    return (
      <div style={S.root}>
        <div style={S.errorBox}>
          <strong>Failed to load vehicles:</strong> {error}
        </div>
      </div>
    );

  return (
    <div style={S.root}>
      <div>
        <div style={S.card}>
          <div style={S.colHead}>Model &amp; variant</div>

          <div style={{ marginBottom: 10 }}>
            {models.map((m) => (
              <button
                key={m}
                style={S.mBtn(selModel === m)}
                onClick={() => handleSelectModel(m)}
              >
                {m}
              </button>
            ))}
          </div>

          {variants.length > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 12,
              }}
            >
              <span style={{ fontSize: 11, color: "#6b7a90", minWidth: 50 }}>
                Variant:
              </span>
              {variants.map((v) => (
                <button
                  key={v}
                  style={S.mBtn(selVariant === v)}
                  onClick={() => handleSelectVariant(v)}
                >
                  {v}
                </button>
              ))}
            </div>
          )}
        </div>

        {doc && (
          <div style={S.card}>
            <InlineField
              label="Deal name"
              value={doc.dealName || ""}
              onChange={(v) => update("dealName", v)}
            />
            <InlineField
              label="MRP (₹)"
              value={doc.mrp || 0}
              type="number"
              onChange={(v) => update("mrp", v)}
            />
            <div style={{ marginTop: 10 }}>
              <div style={{ ...S.colHead, color: "#b45309" }}>
                Add-ons · total: {fmt(computed.addOnTotal)}
              </div>
              <ListEditor
                items={doc.addOnPrices}
                onChange={(v) => update("addOnPrices", v)}
                isDiscount={false}
              />
            </div>
          </div>
        )}

        {doc && (
          <div style={S.card}>
            <div style={{ ...S.colHead, color: "#2e7d32" }}>
              Discounts · total: − {fmt(computed.discTotal)}
            </div>
            <ListEditor
              items={doc.discountPrices}
              onChange={(v) => update("discountPrices", v)}
              isDiscount={true}
            />
          </div>
        )}

        {!doc && (
          <div
            style={{
              ...S.card,
              color: "#8c97a8",
              textAlign: "center",
              padding: 32,
            }}
          >
            Select a model, variant, and color to edit pricing.
          </div>
        )}
      </div>

      <div>
        {doc && (
          <>
            <div style={S.card}>
              <div style={S.secLabel}>Specifications</div>
              <div style={S.specGrid}>
                {[
                  ["Model", selModel],
                  ["Battery", selVariant],
                  // ["Color", selColor],
                ].map(([l, v]) => (
                  <div key={l} style={S.specCell}>
                    <div style={S.sLabel}>{l}</div>
                    <div style={S.sVal}>{v}</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={S.card}>
              <div style={S.secLabel}>Preview of price breakdown</div>
              <PriceLine label="MRP" value={doc.mrp} />
              {doc.addOnPrices
                .filter((a) => a.name)
                .map((a, i) => (
                  <PriceLine key={i} label={`+ ${a.name}`} value={a.value} />
                ))}
              <PriceLine label="Total price" value={computed.totalPrice} bold />
              {doc.discountPrices
                .filter((d) => d.name)
                .map((d, i) => (
                  <PriceLine
                    key={i}
                    label={d.name}
                    value={d.value}
                    isDiscount
                  />
                ))}
              <PriceLine
                label="Total discount"
                value={computed.discTotal}
                bold
                isDiscount
              />

              <div style={S.netBox}>
                <span
                  style={{ fontWeight: 700, fontSize: 15, color: "#1565c0" }}
                >
                  Net payable
                </span>
                <span
                  style={{ fontWeight: 800, fontSize: 22, color: "#1565c0" }}
                >
                  {fmt(computed.netPayable)}
                </span>
              </div>

              {computed.warns.length > 0 ? (
                computed.warns.map((w, i) => (
                  <div key={i} style={S.warn}>
                    ⚠ {w}
                  </div>
                ))
              ) : (
                <div style={S.ok}>✓ Pricing valid</div>
              )}

              <button style={S.saveBtn} onClick={handleSave}>
                {saved ? "Saved!" : "Save pricing"}
              </button>
            </div>
          </>
        )}

        {!doc && (
          <div
            style={{
              ...S.card,
              color: "#8c97a8",
              textAlign: "center",
              padding: 40,
            }}
          >
            Select a vehicle configuration on the left to see the price
            breakdown.
          </div>
        )}
      </div>
    </div>
  );
}
